import React from 'react';
import { Link } from 'react-router-dom';
import { categories } from '../data/mock';
import { PartyPopper, Hammer, Leaf, Sparkles, ArrowRight, Check } from 'lucide-react';
import {UniversIndexGuide} from '../components/FamilyGuide';

const iconMap = {
  party: PartyPopper,
  hammer: Hammer,
  leaf: Leaf,
  sparkles: Sparkles
};

const universDetails = [
  // 1er: Bricolage - Modification #7: Nouvelle image
  {
    id: 1,
    slug: 'bricolage-travaux',
    title: 'Bricolage & Bâtiment',
    description: "Parce que l'achat, l'entretien et le stockage d'outillage lourd ou spécifique peut être contraignant, nous vous proposons une large gamme d'outils pour économiser et gagner en efficacité !",
    image: '/media/adobestock2194254491jpg-63ed61d5d54e3-standard-cc07ae26.jpg',
    features: ['Bétonnière', 'Marteau piqueur', 'Échafaudage', 'Niveau laser', 'Poste à souder', 'Plaque vibrante'],
    color: '#22C55E'
  },
  // 2e: Jardinage - Modification #8: Nouvelle image
  {
    id: 2,
    slug: 'jardinage-motoculture',
    title: 'Motoculture & Jardin',
    description: 'De la préparation de votre terrain à l\'entretien de vos plantations, nous mettons à votre disposition toutes les machines nécessaires au bien-être de vos espaces verts.',
    image: '/media/14-imgp1831-modifier-e1648628779589-high-0wxks3-7a09d5f5.jpg',
    features: ['Motoculteur', 'Broyeur', 'Taille-haie', 'Tronçonneuse', 'Scarificateur', 'Rouleau gazon'],
    color: '#16A34A'
  },
  // 3e: Nettoyage - Modification #9: Nouvelle image
  {
    id: 3,
    slug: 'nettoyage-logement',
    title: 'Nettoyage & Logement',
    description: 'Un diagnostic thermique à réaliser, un déménagement à organiser, un nettoyage efficace ou encore une recherche de coupure de câble électrique ?',
    image: '/media/shampouinneuse-high-f9b565de.jpg',
    features: ['Shampouineuse', 'Karcher', 'Caméra thermique', 'Diable', 'Aspirateur', 'Compresseur'],
    color: '#15803D'
  },
  // 4e: Évènementiel
  {
    id: 4,
    slug: 'evenementiel-barnum',
    title: 'Evénementiel & Loisirs',
    description: 'Anniversaire, Baptême, Mariage, Cérémonie, Réunions, Séminaires. Réservez nos chapiteaux, châteaux gonflables et agencez-les avec notre mobilier, vaisselle et accessoires.',
    image: '/media/couverture-chapiteau-plage-high-1a3ae4b8.jpg',
    features: ['Barnums & Tentes de réception', 'Mobilier & Vaisselle', 'Châteaux gonflables', 'Déco & Guirlandes', 'Sono & Lumières', 'Chauffage'],
    color: '#166534'
  }
];

const UniversPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-6">
            Nos <span className="text-green-400">Univers</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Découvrez nos 4 univers de location pour tous vos projets. Plus de 300 références en stock.
          </p>
        </div>
      </section>

      {/* Categories Grid - Modification #14: Boutons qui redirigent DIRECTEMENT vers la page concernée */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
            {categories.map((category) => {
              const IconComponent = iconMap[category.icon];
              return (
                <Link
                  key={category.id}
                  to={`/univers/${category.slug}`}
                  className="group relative overflow-hidden bg-slate-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
                >
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110 duration-300"
                    style={{ backgroundColor: `${category.color}15` }}
                  >
                    <IconComponent 
                      size={32} 
                      style={{ color: category.color }}
                    />
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">
                    {category.name}
                  </h3>
                  <span 
                    className="text-sm font-medium flex items-center gap-1"
                    style={{ color: category.color }}
                  >
                    Voir le matériel
                    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              );
            })}
          </div>

          {/* Detailed Sections */}
          <div className="space-y-32">
            {universDetails.map((univers, index) => (
              <div 
                key={univers.id}
                id={univers.slug}
                className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}
              >
                {/* Image */}
                <div className={`relative ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div className="relative overflow-hidden rounded-3xl shadow-2xl group">
                    <img
                      src={univers.image}
                      alt={univers.title}
                      className="w-full h-80 lg:h-[500px] object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div 
                      className="absolute inset-0 opacity-20"
                      style={{ background: `linear-gradient(to top, ${univers.color}, transparent)` }}
                    />
                  </div>
                </div>

                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                  <div 
                    className="inline-block w-16 h-1.5 rounded-full mb-6"
                    style={{ backgroundColor: univers.color }}
                  />
                  <h2 className="text-3xl sm:text-4xl font-black text-slate-800 mb-6">
                    {univers.title}
                  </h2>
                  <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                    {univers.description}
                  </p>

                  {/* Features */}
                  <div className="grid grid-cols-2 gap-4 mb-10">
                    {univers.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div 
                          className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${univers.color}20` }}
                        >
                          <Check size={14} style={{ color: univers.color }} />
                        </div>
                        <span className="text-slate-700 font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Button - Un seul bouton - Redirige vers la page univers */}
                  <div className="flex flex-wrap gap-4">
                    <Link
                      to={`/univers/${univers.slug}`}
                      className="group inline-flex items-center gap-2 px-8 py-4 text-white font-bold rounded-xl transition-all hover:shadow-xl transform hover:-translate-y-1"
                      style={{ backgroundColor: univers.color }}
                    >
                      Voir le matériel
                      <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <UniversIndexGuide />
    </div>
  );
};

export default UniversPage;
